// src/pages/VoucherManager.js — Team leader voucher management (rebuilt workflow)
import React, { useState, useEffect, useCallback, useRef } from 'react';
import DayAccounting from './DayAccounting';
import { get, post } from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { Spinner } from '../components/Shared';

const VOUCHER_STATUS_OPTIONS = [
  { value: 'voucher_sgnd',       label: 'Voucher Sgnd' },
  { value: 'retained_by_party',  label: 'Retained by Party' },
  { value: 'voucher_unsgnd',     label: 'Voucher Unsgnd' },
  { value: 'cash',               label: 'Cash' },
  { value: 'cheque',             label: 'Cheque' },
  { value: 'upi',                label: 'UPI' },
  { value: 'rectification',      label: 'Rectification' },
  { value: 'lost_or_mutilated',  label: 'Lost or Mutilated' },
  { value: 'self_pickup',        label: 'Self Pickup by Customer' },
  { value: 'carried_by_rep',     label: 'Carried by Company Rep' },
];

export default function VoucherManager() {
  const navigate = useNavigate();
  // Top-level All/Instation/Outstation tabs (spec item 14.1). 'instation'
  // preserves all existing behavior below unchanged. 'outstation' is new
  // — covers both outstation courier handovers and counter pickups, which
  // previously bypassed vouchers entirely (see routes/dispatch.js fix in
  // this same batch). 'all' shows both sections stacked.
  const [mainTab, setMainTab] = useState('instation');
  const [outstationVouchers, setOutstationVouchers] = useState([]);
  const [outstationLoading, setOutstationLoading] = useState(true);
  const [outstationFrom, setOutstationFrom] = useState('');
  const [outstationTo, setOutstationTo] = useState('');
  const [view,          setView]          = useState('delivery_boys');
  const [groups,        setGroups]        = useState([]);
  // toISOString() is always UTC — adjust by the browser's own offset first
  // so "today" always means the actual local calendar date, matching the
  // pattern already used elsewhere in the system.
  function localDateString(d) {
    const offsetMs = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - offsetMs).toISOString().split('T')[0];
  }
  const today = localDateString(new Date());
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo,   setDateTo]   = useState(today);
  const [voucherSearch, setVoucherSearch] = useState('');
  const [voucherStatusFilter, setVoucherStatusFilter] = useState('');
  const [heatmapSelected, setHeatmapSelected] = useState(new Set());
  const [heatmapTarget, setHeatmapTarget] = useState('voucher_sgnd');
  const [heatmapApplying, setHeatmapApplying] = useState(false);
  const [heatmapSearch, setHeatmapSearch] = useState('');
  const [loading,       setLoading]       = useState(true);
  const [refreshing,    setRefreshing]    = useState(false);
  const [msg,           setMsg]           = useState(null);
  const [scanInput,     setScanInput]     = useState('');
  const [expandedIds,   setExpandedIds]   = useState(new Set());
  const [collapsedRounds, setCollapsedRounds] = useState(new Set());
  const [submitting,    setSubmitting]    = useState(null); // delivery_person_id currently submitting
  // Tracks which groups have had at least one Submit already — after that,
  // leftover unmarked vouchers show checkboxes instead of the chip row.
  const [submittedOnce, setSubmittedOnce] = useState(new Set());
  const [lastScan, setLastScan] = useState(null);            // { ok, text, sub } shown beside the scan box
  const [voucherFilter, setVoucherFilter] = useState('left'); // left | unsigned | signed | all
  const [boySearch, setBoySearch] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const scanRef = useRef(null);
  const firstLoadDone = useRef(false);

  const load = useCallback(async () => {
    if (firstLoadDone.current) setRefreshing(true);
    else setLoading(true);
    try {
      const data = await get('/api/vouchers/returned-deliveries');
      setGroups(data.groups || []);
    } catch (err) { setMsg({ type:'error', text: err.message }); }
    finally {
      setLoading(false);
      setRefreshing(false);
      firstLoadDone.current = true;
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [load]);

  const loadOutstation = useCallback(async () => {
    setOutstationLoading(true);
    try {
      const qs = new URLSearchParams();
      if (outstationFrom) qs.set('from', outstationFrom);
      if (outstationTo) qs.set('to', outstationTo);
      const data = await get('/api/vouchers/outstation-pending' + (qs.toString() ? '?' + qs.toString() : ''));
      setOutstationVouchers(data.vouchers || []);
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setOutstationLoading(false);
    }
  }, [outstationFrom, outstationTo]);

  useEffect(() => {
    if (mainTab === 'outstation' || mainTab === 'all') loadOutstation();
  }, [mainTab, loadOutstation]);

  function toggleExpand(id) {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleRound(key) {
    setCollapsedRounds(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  async function handleBarcodeScan(e) {
    e.preventDefault();
    const inv = scanInput.trim();
    if (!inv) return;

    try {
      const data = await post('/api/vouchers/scan-barcode', { invoice_number: inv });
      // Handler and round come from the list already on screen (looked up before the reload)
      const scanNo = data.invoice_number || inv;
      let scanHit = null;
      for (const g of groups) {
        const hv = (g.vouchers || []).find(x => x.invoice_number === scanNo);
        if (hv) { scanHit = { v: hv, name: g.delivery_person_name }; break; }
      }
      const scanRound = scanHit ? (scanHit.v.round_label ? scanHit.v.round_label : (scanHit.v.round_number ? '#' + scanHit.v.round_number : '')) : '';
      setLastScan({
        ok: true,
        text: `${scanNo} — marked Signed`,
        sub: (data.party_name || (scanHit && scanHit.v.party_name) || ''),
        sub2: (scanHit ? 'Handler: ' + scanHit.name : '') + (scanRound ? ' · Round ' + scanRound : '') + ((scanHit ? ' · ' : '') + new Date().toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true })),
      });
      setScanInput('');
      load();
    } catch (err) {
      setLastScan({ ok: false, text: `${inv} — not marked`, sub: err.message });
      setScanInput('');
    }
    if (scanRef.current) scanRef.current.focus();
  }

  async function handleStatusChange(voucherId, status) {
    try {
      await post(`/api/vouchers/${voucherId}/status`, { voucher_status: status });
      setMsg({ type:'success', text: status ? 'Voucher marked' : 'Selection cleared' });
      setGroups(prev => prev.map(g => ({ ...g, vouchers: (g.vouchers || []).map(v => v.voucher_id === voucherId ? { ...v, voucher_status: status } : v) })));
    } catch (err) { setMsg({ type:'error', text: err.message }); }
  }

  async function handleOutstationStatusChange(voucherId, status) {
    try {
      await post(`/api/vouchers/${voucherId}/status`, { voucher_status: status });
      setOutstationVouchers(prev => prev.map(v => v.voucher_id === voucherId ? { ...v, voucher_status: status } : v));
      loadOutstation(); // take readiness from the server now, not at the next refresh
    } catch (err) { setMsg({ type:'error', text: err.message }); }
  }

  async function handleOutstationComplete(voucher) {
    if (!voucher.voucher_status) {
      setMsg({ type: 'error', text: 'Set a status before marking complete' });
      return;
    }
    try {
      await post(`/api/vouchers/${voucher.voucher_id}/returned`, {});
      setMsg({ type: 'success', text: `${voucher.invoice_number} marked complete` });
      loadOutstation();
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    }
  }

  // Left-over view helpers (Delivery Boys). Left over = not returned, not scanned, nothing marked yet.
  const isLeftOver = (v) => !v.is_returned_to_office && !v.is_signed && !v.voucher_status;
  const isUnsignedMark = (v) => !v.is_returned_to_office && v.voucher_status === 'voucher_unsgnd';
  const isSignedMark = (v) => !v.is_returned_to_office && (v.is_signed || v.voucher_status === 'voucher_sgnd');
  const passesFilter = (v) => {
    if (voucherFilter === 'left' && !isLeftOver(v)) return false;
    if (voucherFilter === 'unsigned' && !isUnsignedMark(v)) return false;
    if (voucherFilter === 'signed' && !isSignedMark(v)) return false;
    if (boySearch) {
      const q = boySearch.toLowerCase();
      if (!(v.invoice_number || '').toLowerCase().includes(q) && !(v.party_name || '').toLowerCase().includes(q)) return false;
    }
    return true;
  };
  function voucherAgeDays(v) {
    if (!v.confirmed_at) return null;
    const istDay = (ms) => Math.floor((ms + 5.5 * 3600000) / 86400000);
    return Math.max(0, istDay(Date.now()) - istDay(new Date(v.confirmed_at).getTime()));
  }
  async function markUnsignedBulk(list, label) {
    if (list.length === 0 || bulkBusy) return;
    if (!window.confirm(`Mark ${list.length} voucher${list.length !== 1 ? 's' : ''} as unsigned?\n${label}\nSigned and already-marked vouchers are not touched.`)) return;
    setBulkBusy(true);
    let done = 0, failed = 0;
    for (const v of list) {
      try { await post(`/api/vouchers/${v.voucher_id}/status`, { voucher_status: 'voucher_unsgnd' }); done += 1; }
      catch (err) { failed += 1; }
    }
    setBulkBusy(false);
    setMsg({ type: failed ? 'error' : 'success', text: `${done} marked unsigned${failed ? `, ${failed} failed` : ''}` });
    load();
  }

  async function handleSubmit(group) {
    setSubmitting(group.delivery_person_id);
    try {
      const data = await post(`/api/vouchers/submit/${group.delivery_person_id}`, {});
      setSubmittedOnce(prev => new Set([...prev, group.delivery_person_id]));
      setMsg({ type:'success', text:`${data.submitted} voucher(s) submitted for ${group.delivery_person_name}` });
      load();
    } catch (err) {
      setMsg({ type:'error', text: err.message });
    } finally {
      setSubmitting(null);
    }
  }

  const allVouchers = groups.flatMap(g => (g.vouchers || []).map(v => ({
    ...v, delivery_person_name: g.delivery_person_name
  })));
  const inDateRange = (v) => {
    if (!dateFrom && !dateTo) return true;
    const d = v.confirmed_at ? v.confirmed_at.slice(0,10) : null;
    if (!d) return true;
    if (dateFrom && d < dateFrom) return false;
    if (dateTo && d > dateTo) return false;
    return true;
  };
  function mngNumTop(v) {
    const digits = (v.invoice_number || '').replace(/[^0-9]/g, '');
    return digits ? parseInt(digits, 10) : 0;
  }
  const returnedVouchers = allVouchers.filter(v => v.is_returned_to_office && inDateRange(v)).sort((a, b) => mngNumTop(a) - mngNumTop(b));
  const pendingVouchers  = allVouchers.filter(v => !v.is_returned_to_office && inDateRange(v)).sort((a, b) => mngNumTop(a) - mngNumTop(b));

  const returnedVouchersFiltered = returnedVouchers.filter(v => {
    if (voucherStatusFilter && v.voucher_status !== voucherStatusFilter) return false;
    if (voucherSearch) {
      const q = voucherSearch.toLowerCase();
      const matchesInvoice = v.invoice_number?.toLowerCase().includes(q);
      const matchesParty = v.party_name?.toLowerCase().includes(q);
      if (!matchesInvoice && !matchesParty) return false;
    }
    return true;
  });

  return (
    <div style={{ width: '100%', minHeight: '100vh', backgroundColor: '#f5f7fa' }}>

      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '24px' }}>

        <button
          onClick={() => navigate(-1)}
          style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
        >
          ← Back
        </button>

        {/* Top-level All/Instation/Outstation tabs — spec item 14.1 */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', height: '52px', boxSizing: 'border-box', position: 'sticky', top: 0, zIndex: 9, backgroundColor: '#f5f7fa' }}>
          {[
            { key: 'instation',  label: 'Instation' },
            { key: 'outstation', label: `Outstation (${outstationVouchers.filter(v => !v.is_returned_to_office).length})` },
            { key: 'all',        label: 'All' },
          ].map(t => (
            <button key={t.key} onClick={() => setMainTab(t.key)}
              style={{
                padding: '9px 18px', borderRadius: '10px', fontSize: '13px', fontWeight: '700', cursor: 'pointer',
                border: mainTab === t.key ? '2px solid #0f172a' : '2px solid #e5e7eb',
                backgroundColor: mainTab === t.key ? '#0f172a' : 'white',
                color: mainTab === t.key ? 'white' : '#4b5563',
              }}>
              {t.label}
            </button>
          ))}
        </div>

        {(mainTab === 'instation' || mainTab === 'all') && (
        <div style={mainTab === 'all' ? { border: '1px solid #e5e7eb', borderRadius: '12px', padding: '16px', marginBottom: '24px', backgroundColor: '#fafafa' } : undefined}>
        {mainTab === 'all' && <p style={{ fontSize: '13px', fontWeight: '700', color: '#374151', margin: '0 0 12px 0', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Instation</p>}

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', height: '64px', boxSizing: 'border-box', position: 'sticky', top: '52px', zIndex: 8, backgroundColor: '#f5f7fa' }}>
          {[
            { key:'delivery_boys', label:`Delivery Boys (${groups.length})` },
            { key:'vouchers',      label:`Voucher Status (${returnedVouchers.length} returned)` },
            { key:'voucher_heatmap', label:`Voucher Heatmap` },
            { key:'day_accounting', label:'Day Accounting' },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setView(t.key)}
              style={{
                padding: '10px 16px',
                borderRadius: '10px',
                fontSize: '14px',
                fontWeight: '600',
                border: view === t.key ? '2px solid #1e3a8a' : '2px solid #e5e7eb',
                backgroundColor: view === t.key ? '#1e3a8a' : 'white',
                color: view === t.key ? 'white' : '#4b5563',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {t.label}
            </button>
          ))}
          <button
            onClick={load}
            style={{ marginLeft: 'auto', padding: '10px 16px', fontSize: '14px', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}
          >
            Refresh
          </button>
        </div>

        {msg && (
          <div style={{
            marginBottom: '16px', padding: '12px 16px', borderRadius: '10px', fontSize: '14px', fontWeight: '500',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            backgroundColor: msg.type === 'success' ? '#f0fdf4' : '#fef2f2',
            color: msg.type === 'success' ? '#15803d' : '#991b1b',
            border: `1px solid ${msg.type === 'success' ? '#86efac' : '#fecaca'}`,
          }}>
            <span>{msg.text}</span>
            <button onClick={() => setMsg(null)} style={{ marginLeft: '12px', opacity: 0.6, background: 'none', border: 'none', fontSize: '18px', lineHeight: 1, cursor: 'pointer', color: 'inherit' }}>×</button>
          </div>
        )}

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}><Spinner /></div>
        ) : view === 'day_accounting' ? (
          <DayAccounting embedded deliveryType={mainTab} />
        ) : view === 'delivery_boys' ? (

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

            {/* Scan is always active — a group only appears once the person
                has a delivery outcome recorded, so there's nothing to gate. */}
            <div style={{ border: '1px solid #fde68a', backgroundColor: '#fffbeb', borderRadius: '10px', padding: '10px 14px', position: 'sticky', top: '116px', zIndex: 7, display: 'flex', gap: '12px', alignItems: 'stretch' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontSize: '13px', fontWeight: '600', color: '#92400e', margin: '0 0 6px 0' }}>Voucher Scan</p>
              <form onSubmit={handleBarcodeScan}>
                <input
                  ref={scanRef}
                  type="text"
                  value={scanInput}
                  onChange={e => setScanInput(e.target.value)}
                  placeholder="Ready — scan barcode with external scanner..."
                  autoComplete="off"
                  autoFocus
                  style={{
                    width: '100%', padding: '8px 12px', border: '2px solid #fcd34d', borderRadius: '8px',
                    fontSize: '14px', outline: 'none', fontFamily: 'monospace', backgroundColor: 'white', boxSizing: 'border-box',
                  }}
                />
              </form>
              </div>
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', borderRadius: '8px', padding: '6px 12px',
                backgroundColor: lastScan ? (lastScan.ok ? '#ecfdf5' : '#fef2f2') : '#fefce8',
                border: '1px solid ' + (lastScan ? (lastScan.ok ? '#6ee7b7' : '#fca5a5') : '#fde68a') }}>
                {lastScan ? (
                  <>
                    <span style={{ fontSize: '13px', fontWeight: '700', color: lastScan.ok ? '#065f46' : '#991b1b', overflowWrap: 'anywhere' }}>{lastScan.ok ? '\u2713 ' : '\u2717 '}{lastScan.text}</span>
                    {lastScan.sub && <span style={{ fontSize: '11px', color: lastScan.ok ? '#047857' : '#b91c1c', overflowWrap: 'anywhere' }}>{lastScan.sub}</span>}
                    {lastScan.sub2 && <span style={{ fontSize: '12px', fontWeight: '600', color: lastScan.ok ? '#065f46' : '#991b1b', overflowWrap: 'anywhere' }}>{lastScan.sub2}</span>}
                  </>
                ) : (
                  <span style={{ fontSize: '12px', color: '#a16207' }}>Scan result appears here</span>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              {[
                ['left', 'Left over', allVouchers.filter(isLeftOver).length],
                ['unsigned', 'Unsigned', allVouchers.filter(isUnsignedMark).length],
                ['signed', 'Signed', allVouchers.filter(isSignedMark).length],
                ['all', 'All', allVouchers.filter(v => !v.is_returned_to_office).length],
              ].map(([k, label, n]) => (
                <button key={k} onClick={() => setVoucherFilter(k)}
                  style={{ padding: '6px 12px', borderRadius: '999px', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                    border: voucherFilter === k ? '1px solid ' + (k === 'left' ? '#b91c1c' : '#1e3a8a') : '1px solid #d1d5db',
                    backgroundColor: voucherFilter === k ? (k === 'left' ? '#b91c1c' : '#1e3a8a') : 'white',
                    color: voucherFilter === k ? 'white' : '#374151' }}>
                  {label} ({n})
                </button>
              ))}
              <input type="text" value={boySearch} onChange={e => setBoySearch(e.target.value)} placeholder="Search party or bill no..."
                style={{ marginLeft: 'auto', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '12px', width: '220px', outline: 'none' }} />
            </div>

            {groups.length === 0 ? (
              <div style={{ backgroundColor: 'white', borderRadius: '10px', border: '2px dashed #e5e7eb', padding: '48px 20px', textAlign: 'center' }}>
                
                <p style={{ color: '#6b7280', fontWeight: '600', margin: 0 }}>No delivery boys with pending vouchers</p>
                <p style={{ color: '#9ca3af', fontSize: '13px', margin: '6px 0 0 0' }}>Vouchers appear here once a delivery outcome is recorded in the app</p>
              </div>
            ) : groups.map(group => {
              const expanded = expandedIds.has(group.delivery_person_id);
              const vouchers = group.vouchers || [];
              const hasSubmittedBefore = false; // capsule selector always (v4)
              const readyToSubmitCount = vouchers.filter(v => v.is_ready_to_submit).length;
              const leftOverList = vouchers.filter(isLeftOver);

              return (
                <div key={group.delivery_person_id} style={{ backgroundColor: 'white', borderRadius: '10px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>

                  <div style={{ backgroundColor: '#eff6ff', borderBottom: '1px solid #dbeafe', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                    <button
                      onClick={() => toggleExpand(group.delivery_person_id)}
                      style={{ flex: 1, textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                        <span style={{ fontSize: '13px', marginTop: '4px', display: 'inline-block', transition: 'transform 0.2s', transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                        <div>
                          <p style={{ fontWeight: '700', color: '#1e3a8a', fontSize: '17px', margin: 0 }}>{group.delivery_person_name}</p>
                          <p style={{ fontSize: '13px', color: '#2563eb', margin: '2px 0 0 0' }}>{group.delivery_person_phone}</p>
                          <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '11px', fontWeight: '600', backgroundColor: '#dbeafe', color: '#1d4ed8', padding: '3px 10px', borderRadius: '999px' }}>
                              {group.total_vouchers} voucher{group.total_vouchers !== 1 ? 's' : ''}
                            </span>
                            {group.pending_count > 0 && (
                              <span style={{ fontSize: '11px', fontWeight: '600', backgroundColor: '#fef3c7', color: '#92400e', padding: '3px 10px', borderRadius: '999px' }}>
                                {group.pending_count} pending
                              </span>
                            )}
                            {leftOverList.length > 0 && (
                              <span style={{ fontSize: '11px', fontWeight: '600', backgroundColor: '#fee2e2', color: '#991b1b', padding: '3px 10px', borderRadius: '999px' }}>
                                {leftOverList.length} left over
                              </span>
                            )}
                            {group.returned_count > 0 && (
                              <span style={{ fontSize: '11px', fontWeight: '600', backgroundColor: '#dcfce7', color: '#166534', padding: '3px 10px', borderRadius: '999px' }}>
                                {group.returned_count} returned
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>

                    {leftOverList.length > 0 && (
                      <button
                        onClick={() => markUnsignedBulk(leftOverList, group.delivery_person_name + ', all rounds')}
                        disabled={bulkBusy}
                        style={{ flexShrink: 0, padding: '10px 14px', borderRadius: '10px', fontSize: '12px', fontWeight: '700', border: 'none', backgroundColor: '#b45309', color: 'white', cursor: bulkBusy ? 'wait' : 'pointer' }}
                      >
                        {bulkBusy ? 'Working\u2026' : `Mark remaining as unsigned (${leftOverList.length})`}
                      </button>
                    )}
                    <button
                      onClick={() => handleSubmit(group)}
                      disabled={submitting === group.delivery_person_id || readyToSubmitCount === 0}
                      style={{
                        flexShrink: 0, padding: '10px 18px', borderRadius: '10px', fontSize: '13px', fontWeight: '700', border: 'none',
                        backgroundColor: readyToSubmitCount === 0 ? '#e5e7eb' : '#059669',
                        color: readyToSubmitCount === 0 ? '#9ca3af' : 'white',
                        cursor: readyToSubmitCount === 0 ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {submitting === group.delivery_person_id ? 'Submitting…' : `Submit${readyToSubmitCount > 0 ? ` (${readyToSubmitCount})` : ''}`}
                    </button>
                  </div>

                  {expanded && (
                    <div>
                      {vouchers.filter(v => !v.is_returned_to_office).length === 0 ? (
                        <p style={{ padding: '16px 20px', fontSize: '14px', color: '#16a34a', margin: 0 }}>All vouchers returned — see Voucher Status tab</p>
                      ) : (() => {
                        // Group by dispatch round so vouchers picked up in
                        // the same round stay together, instead of one
                        // flat list mixing multiple rounds for this person.
                        const active = vouchers.filter(v => !v.is_returned_to_office && passesFilter(v));
                        if (active.length === 0) return (
                          <p style={{ padding: '16px 20px', fontSize: '13px', color: '#6b7280', margin: 0 }}>Nothing here for this filter. Choose All to see every voucher.</p>
                        );
                        const roundGroups = [];
                        const byRound = {};
                        for (const v of active) {
                          const key = v.round_id || 'unassigned';
                          if (!byRound[key]) {
                            byRound[key] = { round_id: v.round_id, round_number: v.round_number, round_label: v.round_label, items: [] };
                            roundGroups.push(byRound[key]);
                          }
                          byRound[key].items.push(v);
                        }
                        roundGroups.sort((a, b) => ((!a.round_label && !b.round_label) ? 0 : (!a.round_label ? 1 : (!b.round_label ? -1 : a.round_label.localeCompare(b.round_label)))));

                        return roundGroups.map((rg, gi) => {
                          const roundKey = rg.round_id || `unassigned-${gi}`;
                          const isCollapsed = collapsedRounds.has(roundKey);
                          return (
                          <div key={roundKey}>
                            <button
                              onClick={() => toggleRound(roundKey)}
                              style={{ width: '100%', textAlign: 'left', padding: '8px 20px', backgroundColor: '#f9fafb', borderTop: gi > 0 ? '1px solid #e5e7eb' : 'none', borderBottom: '1px solid #f3f4f6', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                            >
                              <span style={{ fontSize: '11px', transition: 'transform 0.15s', display: 'inline-block', transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}>▼</span>
                              <span style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase' }}>
                                {rg.round_label ? `Round ${rg.round_label}` : (rg.round_number ? `Round #${rg.round_number}` : 'Unassigned Round')}
                              </span>
                              <span style={{ fontSize: '11px', fontWeight: '600', color: '#9ca3af' }}>
                                ({rg.items.length} voucher{rg.items.length !== 1 ? 's' : ''})
                              </span>
                            </button>
                            {(() => {
                              const roundLeft = vouchers.filter(x => (x.round_id || 'unassigned') === (rg.round_id || 'unassigned') && isLeftOver(x));
                              return roundLeft.length > 0 ? (
                                <div style={{ padding: '4px 20px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px', backgroundColor: '#fffbeb' }}>
                                  <span style={{ fontSize: '11px', color: '#92400e' }}>{roundLeft.length} left over in this round</span>
                                  <button onClick={() => markUnsignedBulk(roundLeft, (rg.round_label ? 'Round ' + rg.round_label : 'This round'))} disabled={bulkBusy}
                                    style={{ padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '700', border: '1px solid #fcd34d', backgroundColor: 'white', color: '#92400e', cursor: bulkBusy ? 'wait' : 'pointer' }}>
                                    Mark these {roundLeft.length} unsigned
                                  </button>
                                </div>
                              ) : null;
                            })()}
                            {!isCollapsed && rg.items.map((v, i) => {
                        return (
                          <div key={v.voucher_id} style={{ padding: '16px 20px', display: 'flex', gap: '20px', alignItems: 'flex-start', borderTop: i > 0 ? '1px solid #f3f4f6' : 'none' }}>

                            <div style={{ flex: '0 0 260px', minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <p style={{ fontFamily: 'monospace', fontSize: '15px', fontWeight: '700', color: '#1f2937', margin: 0 }}>{v.invoice_number}</p>
                                {!v.is_returned_to_office && voucherAgeDays(v) !== null && (
                                  <span style={{ fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '6px',
                                    backgroundColor: voucherAgeDays(v) >= 3 ? '#fee2e2' : (voucherAgeDays(v) === 2 ? '#fef3c7' : '#f3f4f6'),
                                    color: voucherAgeDays(v) >= 3 ? '#991b1b' : (voucherAgeDays(v) === 2 ? '#92400e' : '#374151') }}>
                                    {voucherAgeDays(v) === 0 ? 'today' : voucherAgeDays(v) + ' d'}
                                  </span>
                                )}
                                {v.is_returned_to_office && (
                                  <span style={{ fontSize: '11px', fontWeight: '600', backgroundColor: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: '999px' }}>Returned</span>
                                )}
                                {v.is_signed && !v.is_returned_to_office && (
                                  <span style={{ fontSize: '11px', fontWeight: '600', backgroundColor: '#dbeafe', color: '#1d4ed8', padding: '2px 8px', borderRadius: '999px' }}>Signed</span>
                                )}
                              </div>
                              <p style={{ fontSize: '12px', color: '#6b7280', margin: '6px 0 0 0' }}>{v.party_name}</p>
                              <p style={{ fontSize: '12px', color: '#9ca3af', margin: '2px 0 0 0' }}>{v.city}</p>
                              {v.voucher_status && (
                                <span style={{ display: 'inline-block', marginTop: '6px', fontSize: '11px', fontWeight: '600', backgroundColor: '#f3e8ff', color: '#7e22ce', padding: '2px 8px', borderRadius: '999px' }}>
                                  {VOUCHER_STATUS_OPTIONS.find(o => o.value === v.voucher_status)?.label || v.voucher_status.replace(/_/g, ' ')}
                                </span>
                              )}
                            </div>

                            {v.is_returned_to_office ? (
                              <div style={{ flex: 1 }} />
                            ) : v.is_signed ? (
                              // Already scanned — waiting for Submit, nothing to pick
                              <div style={{ flex: 1, fontSize: '12px', color: '#2563eb', paddingTop: '8px' }}>
                                Scanned — will be submitted on next Submit
                              </div>
                            ) : !hasSubmittedBefore ? (
                              // Before any Submit for this group: full chip selector available
                              <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                {VOUCHER_STATUS_OPTIONS.map(o => {
                                  const selected = v.voucher_status === o.value;
                                  return (
                                    <button
                                      key={o.value}
                                      onClick={() => handleStatusChange(v.voucher_id, selected ? null : o.value)}
                                      style={{
                                        padding: '8px 14px', borderRadius: '999px', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                                        border: selected ? '2px solid #1e3a8a' : '1px solid #d1d5db',
                                        backgroundColor: selected ? '#1e3a8a' : 'white',
                                        color: selected ? 'white' : '#4b5563',
                                        transition: 'all 0.15s', whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {selected ? '✓ ' : ''}{o.label}
                                    </button>
                                  );
                                })}
                              </div>
                            ) : (
                              // After a Submit already happened: leftover stragglers get checkboxes
                              <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                {VOUCHER_STATUS_OPTIONS.map(o => {
                                  const selected = v.voucher_status === o.value;
                                  return (
                                    <label
                                      key={o.value}
                                      style={{
                                        display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px',
                                        borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                                        border: selected ? '2px solid #1e3a8a' : '1px solid #d1d5db',
                                        backgroundColor: selected ? '#eef2ff' : 'white', color: '#374151',
                                      }}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={selected}
                                        onChange={() => handleStatusChange(v.voucher_id, selected ? null : o.value)}
                                        style={{ margin: 0 }}
                                      />
                                      {o.label}
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                            })}
                          </div>
                          );
                        });
                      })()}
                    </div>
                  )}

                  {!expanded && (
                    <div style={{ padding: '10px 20px', backgroundColor: '#f9fafb', fontSize: '12px', color: '#9ca3af', borderTop: '1px solid #f3f4f6' }}>
                      Click name to view vouchers
                    </div>
                  )}
                </div>
              );
            })}
          </div>

        ) : view === 'vouchers' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '14px 16px', position: 'sticky', top: '116px', zIndex: 7 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ fontSize: '13px', color: '#6b7280' }}>From</label>
                <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                  style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', outline: 'none' }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ fontSize: '13px', color: '#6b7280' }}>To</label>
                <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                  style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', outline: 'none' }} />
              </div>
              {(dateFrom || dateTo) && (
                <button onClick={() => { setDateFrom(''); setDateTo(''); }}
                  style={{ padding: '8px 14px', fontSize: '13px', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', color: '#4b5563' }}>
                  Clear
                </button>
              )}
              <button onClick={() => { setDateFrom(today); setDateTo(today); }}
                style={{ padding: '8px 14px', fontSize: '13px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', color: '#1d4ed8' }}>
                Today
              </button>
              <input
                type="text"
                value={voucherSearch}
                onChange={e => setVoucherSearch(e.target.value)}
                placeholder="Search invoice # or party name..."
                style={{ flex: 1, minWidth: '220px', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', outline: 'none' }}
              />
              <select value={voucherStatusFilter} onChange={e => setVoucherStatusFilter(e.target.value)}
                style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', outline: 'none', backgroundColor: 'white' }}>
                <option value="">All Statuses</option>
                {VOUCHER_STATUS_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
                <p style={{ fontSize: '28px', fontWeight: '700', color: '#15803d', margin: 0 }}>{returnedVouchers.length}</p>
                <p style={{ fontSize: '13px', color: '#16a34a', fontWeight: '600', margin: '4px 0 0 0' }}>Vouchers Returned</p>
              </div>
              <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
                <p style={{ fontSize: '28px', fontWeight: '700', color: '#b45309', margin: 0 }}>{pendingVouchers.length}</p>
                <p style={{ fontSize: '13px', color: '#d97706', fontWeight: '600', margin: '4px 0 0 0' }}>Pending Return</p>
              </div>
            </div>

            {returnedVouchersFiltered.length === 0 ? (
              <div style={{ backgroundColor: 'white', borderRadius: '10px', border: '2px dashed #e5e7eb', padding: '48px 20px', textAlign: 'center' }}>
                <p style={{ fontSize: '36px', margin: '0 0 12px 0' }}>—</p>
                <p style={{ color: '#6b7280', fontWeight: '600', margin: 0 }}>
                  {voucherSearch || voucherStatusFilter ? 'No vouchers match this search/filter' : 'No vouchers returned yet'}
                </p>
              </div>
            ) : (
              <div style={{ backgroundColor: 'white', borderRadius: '10px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                  <p style={{ fontSize: '14px', fontWeight: '600', color: '#374151', margin: 0 }}>
                    Returned Vouchers {(voucherSearch || voucherStatusFilter) && `(${returnedVouchersFiltered.length} of ${returnedVouchers.length})`}
                  </p>
                </div>
                <div>
                  {returnedVouchersFiltered.map((v, i) => (
                    <div key={v.voucher_id} style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: i > 0 ? '1px solid #f3f4f6' : 'none' }}>
                      <div>
                        <p style={{ fontFamily: 'monospace', fontSize: '14px', fontWeight: '600', color: '#1f2937', margin: 0 }}>{v.invoice_number}</p>
                        <p style={{ fontSize: '12px', color: '#6b7280', margin: '2px 0 0 0' }}>{v.party_name} · {v.delivery_person_name}</p>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {v.voucher_status && (
                          <span style={{ fontSize: '11px', fontWeight: '600', backgroundColor: '#f3e8ff', color: '#7e22ce', padding: '2px 8px', borderRadius: '999px' }}>
                            {VOUCHER_STATUS_OPTIONS.find(o => o.value === v.voucher_status)?.label || v.voucher_status.replace(/_/g, ' ')}
                          </span>
                        )}
                        <span style={{ fontSize: '11px', fontWeight: '600', backgroundColor: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: '999px' }}>Returned</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          (() => {
            // Voucher Heatmap filters by the invoice's own original date,
            // not confirmed_at — matches the workflow of bulk-completing
            // older invoices and wanting to find them by their real date,
            // not the date they happened to get processed.
            const inInvoiceDateRange = (v) => {
              if (!dateFrom && !dateTo) return true;
              const d = v.invoice_date ? String(v.invoice_date).slice(0,10) : null;
              if (!d) return true;
              if (dateFrom && d < dateFrom) return false;
              if (dateTo && d > dateTo) return false;
              return true;
            };
            const heatmapFiltered = allVouchers.filter(v => {
              if (!inInvoiceDateRange(v)) return false;
              if (!heatmapSearch) return true;
              const q = heatmapSearch.toLowerCase();
              return v.invoice_number?.toLowerCase().includes(q) || v.party_name?.toLowerCase().includes(q);
            });
            const buckets = [
              ...VOUCHER_STATUS_OPTIONS,
              { value: null, label: 'Unset / Pending' },
              { value: 'returned_no_status', label: 'Submitted (No Status Set)' },
            ];
            // Sort by invoice number numerically (MNG28162 -> 28162) so
            // the heatmap reads in a sensible order instead of whatever
            // order the API happened to return rows in.
            function mngNum(v) {
              const digits = (v.invoice_number || '').replace(/[^0-9]/g, '');
              return digits ? parseInt(digits, 10) : 0;
            }
            const sortByInvoice = (arr) => [...arr].sort((a, b) => mngNum(a) - mngNum(b));
            // Effective status comes from the backend (v_voucher_state.confirmation_type) - one definition for every page.
            const effStatus = (v) => v.confirmation_type || null;
            const grouped = buckets.reduce((acc, b) => {
              if (b.value === 'returned_no_status') {
                // Vouchers finalized via the scan-and-submit path
                // (is_signed) never get a manual voucher_status set, so
                // they were incorrectly landing in "Unset / Pending"
                // forever even though the team leader already submitted
                // them — this bucket separates "genuinely done, just
                // never had a status" from "actually still pending".
                acc[b.label] = sortByInvoice(heatmapFiltered.filter(v => v.is_returned_to_office && !effStatus(v)));
              } else if (b.value === null) {
                acc[b.label] = sortByInvoice(heatmapFiltered.filter(v => !v.is_returned_to_office && !effStatus(v)));
              } else {
                acc[b.label] = sortByInvoice(heatmapFiltered.filter(v => effStatus(v) === b.value));
              }
              return acc;
            }, {});

            function toggle(voucherId) {
              setHeatmapSelected(prev => {
                const next = new Set(prev);
                next.has(voucherId) ? next.delete(voucherId) : next.add(voucherId);
                return next;
              });
            }
            function selectAllInBucket(label) {
              const ids = grouped[label].map(v => v.voucher_id);
              const allSel = ids.every(id => heatmapSelected.has(id));
              setHeatmapSelected(prev => {
                const next = new Set(prev);
                if (allSel) ids.forEach(id => next.delete(id));
                else ids.forEach(id => next.add(id));
                return next;
              });
            }
            async function applyHeatmapOverride() {
              if (heatmapSelected.size === 0) return;
              setHeatmapApplying(true);
              try {
                const res = await post('/api/vouchers/bulk-status', {
                  voucher_ids: Array.from(heatmapSelected),
                  voucher_status: heatmapTarget,
                });
                setMsg({
                  type: res.failed > 0 ? 'error' : 'success',
                  text: res.message,
                });
                setHeatmapSelected(new Set());
                await load();
              } catch (err) {
                setMsg({ type: 'error', text: err.message });
              } finally {
                setHeatmapApplying(false);
              }
            }

            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '14px 16px', position: 'sticky', top: '116px', zIndex: 7 }}>
                  <input type="text" value={heatmapSearch} onChange={e => setHeatmapSearch(e.target.value)}
                    placeholder="Search invoice # or party name..."
                    style={{ flex: 1, minWidth: '220px', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', outline: 'none' }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <label style={{ fontSize: '13px', color: '#6b7280', fontWeight: '600' }}>From</label>
                    <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                      style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px' }} />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <label style={{ fontSize: '13px', color: '#6b7280', fontWeight: '600' }}>To</label>
                    <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                      style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px' }} />
                  </div>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', backgroundColor: '#1e3a8a', padding: '14px', borderRadius: '10px' }}>
                  <span style={{ color: 'white', fontSize: '14px', fontWeight: '600' }}>
                    {heatmapSelected.size} voucher{heatmapSelected.size !== 1 ? 's' : ''} selected
                  </span>
                  <select value={heatmapTarget} onChange={e => setHeatmapTarget(e.target.value)}
                    style={{ padding: '8px 12px', borderRadius: '8px', border: 'none', fontSize: '13px', fontWeight: '600' }}>
                    {VOUCHER_STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <button onClick={applyHeatmapOverride} disabled={heatmapSelected.size === 0 || heatmapApplying}
                    style={{
                      padding: '8px 20px', backgroundColor: heatmapSelected.size === 0 || heatmapApplying ? '#93c5fd' : '#fbbf24',
                      color: '#1e3a8a', fontWeight: '700', border: 'none', borderRadius: '8px', fontSize: '13px',
                      cursor: heatmapSelected.size === 0 || heatmapApplying ? 'default' : 'pointer',
                    }}>
                    {heatmapApplying ? 'Applying...' : `Apply to ${heatmapSelected.size} Selected`}
                  </button>
                  {heatmapSelected.size > 0 && (
                    <button onClick={() => setHeatmapSelected(new Set())}
                      style={{ padding: '8px 14px', backgroundColor: 'rgba(255,255,255,0.15)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>
                      Clear Selection
                    </button>
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '14px' }}>
                  {buckets.map(b => {
                    const items = grouped[b.label] || [];
                    const allSel = items.length > 0 && items.every(v => heatmapSelected.has(v.voucher_id));
                    return (
                      <div key={b.label} style={{ backgroundColor: 'white', borderRadius: '10px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                        <div style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <p style={{ fontWeight: '700', fontSize: '12px', color: '#1f2937', margin: 0 }}>{b.label}</p>
                            <p style={{ fontSize: '10px', color: '#9ca3af', margin: '1px 0 0 0' }}>{items.length} voucher{items.length !== 1 ? 's' : ''}</p>
                          </div>
                          {items.length > 0 && (
                            <button onClick={() => selectAllInBucket(b.label)}
                              style={{ fontSize: '10px', fontWeight: '600', color: '#1d4ed8', background: 'none', border: 'none', cursor: 'pointer' }}>
                              {allSel ? 'Deselect' : 'Select All'}
                            </button>
                          )}
                        </div>
                        <div style={{ padding: '8px', maxHeight: '320px', overflowY: 'auto' }}>
                          {items.length === 0 ? (
                            <p style={{ fontSize: '11px', color: '#9ca3af', textAlign: 'center', padding: '12px 0' }}>None</p>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                              {items.map(v => {
                                const sel = heatmapSelected.has(v.voucher_id);
                                return (
                                  <button key={v.voucher_id} onClick={() => toggle(v.voucher_id)}
                                    style={{
                                      textAlign: 'left', padding: '7px 9px', borderRadius: '7px', fontSize: '11px',
                                      border: `1.5px solid ${sel ? '#1e3a8a' : '#e5e7eb'}`, cursor: 'pointer',
                                      backgroundColor: sel ? '#1e3a8a' : '#fafafa', color: sel ? 'white' : '#1f2937',
                                    }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                      <span style={{ fontFamily: 'monospace', fontWeight: '700' }}>{v.invoice_number}</span>
                                      {sel && <span>✓</span>}
                                    </div>
                                    <p style={{ margin: '2px 0 0 0', opacity: 0.8, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.party_name}</p>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()
        )}
        </div>
        )}

        {(mainTab === 'outstation' || mainTab === 'all') && (
          <OutstationVouchersSection
            vouchers={outstationVouchers}
            loading={outstationLoading}
            onStatusChange={handleOutstationStatusChange}
            onComplete={handleOutstationComplete}
            onRefresh={loadOutstation}
            from={outstationFrom}
            to={outstationTo}
            onRangeChange={(f, t) => { setOutstationFrom(f); setOutstationTo(t); }}
            titled={mainTab === 'all'}
          />
        )}
      </div>
    </div>
  );
}

// Outstation/counter voucher final-status marking — spec item 14.3. These
// vouchers have no "delivery person returns to office" step (unlike
// instation), so they're a flat list rather than grouped/round-based.
function OutstationVouchersSection({ vouchers, loading, onStatusChange, onComplete, onRefresh, titled, from = '', to = '', onRangeChange }) {
  const [scanValue, setScanValue] = useState('');
  const [scanMsg, setScanMsg] = useState(null);
  const [scanBusy, setScanBusy] = useState(false);

  async function handleScan(e) {
    e.preventDefault();
    const code = scanValue.trim();
    if (!code || scanBusy) return;
    setScanBusy(true);
    try {
      const res = await post('/api/vouchers/scan-barcode', { invoice_number: code });
      setScanMsg({ type: 'success', text: (res && res.message) ? ((res.invoice_number || code) + ': ' + res.message) : (code + ' scanned') });
      onRefresh();
    } catch (err) {
      setScanMsg({ type: 'error', text: err.message });
    } finally {
      setScanValue('');
      setScanBusy(false);
      setTimeout(() => { const el = document.getElementById('outstation-scan-input'); if (el) el.focus(); }, 0);
    }
  }

  async function handleSubmitAll() {
    const ids = vouchers.filter(v => v.is_ready_to_submit).map(v => v.voucher_id);
    if (!ids.length || scanBusy) return;
    setScanBusy(true);
    try {
      const res = await post('/api/vouchers/submit-outstation', { voucher_ids: ids });
      setScanMsg({ type: 'success', text: (res && res.message) ? res.message : 'Submitted' });
      onRefresh();
    } catch (err) {
      setScanMsg({ type: 'error', text: err.message });
    } finally {
      setScanBusy(false);
      setTimeout(() => { const el = document.getElementById('outstation-scan-input'); if (el) el.focus(); }, 0);
    }
  }
  const readyCount = vouchers.filter(v => v.is_ready_to_submit).length;

  const fmtDate = d => {
    const pad = n => String(n).padStart(2, '0');
    return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
  };
  function setQuick(days) {
    if (!onRangeChange) return;
    if (days === null) { onRangeChange('', ''); return; }
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - (days - 1));
    onRangeChange(fmtDate(start), fmtDate(end));
  }
  const dateInputStyle = { padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px' };
  const quickBtnStyle = { padding: '7px 12px', fontSize: '12px', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' };

  const pending = vouchers.filter(v => !v.is_returned_to_office);
  const done = vouchers.filter(v => v.is_returned_to_office);

  return (
    <div>
      {titled && <p style={{ fontSize: '13px', fontWeight: '700', color: '#374151', margin: '0 0 12px 0', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Outstation</p>}

      {!titled && (
        <form onSubmit={handleScan} style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
          <input
            value={scanValue}
            onChange={e => setScanValue(e.target.value)}
            placeholder="Scan outstation voucher (marks Voucher Unsgnd)"
            autoFocus
            id="outstation-scan-input"
            style={{ flex: 1, minWidth: '240px', padding: '10px 12px', border: '2px solid #1e3a8a', borderRadius: '8px', fontSize: '14px', fontFamily: 'monospace' }}
          />
          <button type="submit" disabled={scanBusy || !scanValue.trim()}
            style={{ padding: '10px 18px', borderRadius: '8px', fontSize: '13px', fontWeight: '700', border: 'none', backgroundColor: '#1e3a8a', color: 'white', cursor: 'pointer' }}>
            Scan
          </button>
        </form>
      )}
      {scanMsg && (
        <div style={{ marginBottom: '10px', padding: '8px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: '600',
          backgroundColor: scanMsg.type === 'error' ? '#fee2e2' : '#dcfce7', color: scanMsg.type === 'error' ? '#991b1b' : '#166534' }}>
          {scanMsg.text}
        </div>
      )}
      {!titled && onRangeChange && (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '12px' }}>
          <span style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Bill date</span>
          <input type="date" value={from} onChange={e => onRangeChange(e.target.value, to)} style={dateInputStyle} />
          <span style={{ fontSize: '12px', color: '#6b7280' }}>to</span>
          <input type="date" value={to} onChange={e => onRangeChange(from, e.target.value)} style={dateInputStyle} />
          {[['Today', 1], ['7 days', 7], ['30 days', 30], ['All', null]].map(([label, d]) => (
            <button key={label} type="button" onClick={() => setQuick(d)} style={quickBtnStyle}>{label}</button>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '12px' }}>
        <button onClick={handleSubmitAll} disabled={scanBusy || readyCount === 0}
          style={{
            padding: '8px 18px', borderRadius: '10px', fontSize: '13px', fontWeight: '700', border: 'none',
            backgroundColor: readyCount === 0 ? '#e5e7eb' : '#059669',
            color: readyCount === 0 ? '#9ca3af' : 'white',
            cursor: readyCount === 0 ? 'not-allowed' : 'pointer',
          }}>
          {scanBusy ? 'Working…' : `Submit${readyCount > 0 ? ` (${readyCount})` : ''}`}
        </button>
        <button onClick={onRefresh}
          style={{ padding: '8px 14px', fontSize: '13px', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>
          Refresh
        </button>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}><Spinner /></div>
      ) : pending.length === 0 ? (
        <div style={{ backgroundColor: 'white', borderRadius: '10px', border: '2px dashed #e5e7eb', padding: '40px 20px', textAlign: 'center' }}>
          <p style={{ color: '#6b7280', fontWeight: '600', margin: 0 }}>No outstation vouchers pending final status</p>
          <p style={{ color: '#9ca3af', fontSize: '13px', margin: '6px 0 0 0' }}>Courier handovers and counter pickups appear here awaiting completion</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {pending.map(v => (
            <div key={v.voucher_id} style={{ backgroundColor: 'white', borderRadius: '10px', border: '1px solid #e5e7eb', padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <div style={{ minWidth: '140px' }}>
                <p style={{ fontFamily: 'monospace', fontWeight: '700', fontSize: '13px', margin: 0 }}>{v.invoice_number}</p>
                <p style={{ fontSize: '12px', color: '#6b7280', margin: '2px 0 0 0' }}>{v.party_name}</p>
              </div>
              <div style={{ minWidth: '120px' }}>
                <span style={{ fontSize: '11px', fontWeight: '600', padding: '3px 8px', borderRadius: '999px', backgroundColor: v.outcome === 'counter_pickup' ? '#fef3c7' : '#e0e7ff', color: v.outcome === 'counter_pickup' ? '#92400e' : '#3730a3' }}>
                  {v.outcome === 'counter_pickup' ? 'Counter Pickup' : (v.transport_name || 'Outstation')}
                </span>
                <p style={{ fontSize: '11px', color: '#9ca3af', margin: '4px 0 0 0' }}>
                  {v.confirmed_at ? new Date(v.confirmed_at).toLocaleDateString('en-IN') : ''}
                </p>
              </div>
              <div style={{ flex: 1, minWidth: '240px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {VOUCHER_STATUS_OPTIONS.map(o => {
                  const selected = v.voucher_status === o.value;
                  return (
                    <button
                      key={o.value}
                      onClick={() => {
                        onStatusChange(v.voucher_id, selected ? null : o.value);
                        setTimeout(() => { const el = document.getElementById('outstation-scan-input'); if (el) el.focus(); }, 0);
                      }}
                      style={{
                        padding: '8px 14px', borderRadius: '999px', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                        border: selected ? '2px solid #1e3a8a' : '1px solid #d1d5db',
                        backgroundColor: selected ? '#1e3a8a' : 'white',
                        color: selected ? 'white' : '#4b5563',
                        transition: 'all 0.15s', whiteSpace: 'nowrap',
                      }}
                    >
                      {selected ? '✓ ' : ''}{o.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {done.length > 0 && (
        <details style={{ marginTop: '20px' }}>
          <summary style={{ fontSize: '13px', fontWeight: '600', color: '#6b7280', cursor: 'pointer' }}>
            {done.length} completed
          </summary>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '10px' }}>
            {done.map(v => (
              <div key={v.voucher_id} style={{ padding: '8px 14px', backgroundColor: '#f9fafb', borderRadius: '8px', fontSize: '12px', color: '#6b7280', display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontFamily: 'monospace' }}>{v.invoice_number} — {v.party_name}</span>
                <span>{v.voucher_status ? v.voucher_status.replace(/_/g, ' ') : ''}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
